import { useState } from "react";
import { ArrowRight, Loader2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { filterLead } from "@/lib/filterLead.functions";

export const HeroForm = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "MN",
    zip_code: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.address || !form.city || !form.zip_code) {
      toast.error("Please fill out all fields.");
      return;
    }
    setLoading(true);
    const { data: inserted, error } = await supabase
      .from("form_leads")
      .insert({ ...form, source: "hero_form" })
      .select("id")
      .single();
    setLoading(false);
    if (error) {
      toast.error("Something went wrong. Please try again.");
      return;
    }
    if (inserted?.id) {
      filterLead({ data: { lead_id: inserted.id } }).catch(() => {});
    }
    setSubmitted(true);
    toast.success("Estimate request submitted!");
  };

  if (submitted) {
    return (
      <div className="bg-card/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-border p-6 lg:p-8 text-center">
        <CheckCircle className="w-12 h-12 text-secondary mx-auto mb-4" />
        <h3 className="text-xl font-heading font-bold text-foreground mb-2">Thank You!</h3>
        <p className="text-muted-foreground">We'll get back to you within 1 business day with your free estimate.</p>
      </div>
    );
  }

  return (
    <div className="bg-card/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-border p-4 lg:p-6">
      <h3 className="text-lg font-heading font-bold text-foreground mb-4 text-center">Get a Free Estimate</h3>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input name="first_name" placeholder="First Name *" value={form.first_name} onChange={handleChange} required maxLength={100} />
          <Input name="last_name" placeholder="Last Name *" value={form.last_name} onChange={handleChange} required maxLength={100} />
        </div>
        <Input name="email" type="email" placeholder="Email *" value={form.email} onChange={handleChange} required maxLength={255} />
        <Input name="phone" type="tel" placeholder="Phone *" value={form.phone} onChange={handleChange} required maxLength={20} />
        <Input name="address" placeholder="Street Address *" value={form.address} onChange={handleChange} required maxLength={255} />
        <div className="grid grid-cols-3 gap-3">
          <Input name="city" placeholder="City *" value={form.city} onChange={handleChange} required maxLength={100} />
          <Input name="state" placeholder="State" value={form.state} onChange={handleChange} maxLength={2} />
          <Input name="zip_code" placeholder="Zip *" value={form.zip_code} onChange={handleChange} required maxLength={10} />
        </div>
        <Button type="submit" className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold" disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Get My Free Estimate <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </form>
    </div>
  );
};
